import { ref } from 'vue';
import { useArloService } from '@/services/arloapi.js';
import { useErrorHandler } from '@/composables/useErrorHandler.js';
import { showLogConsole } from '@/commons/utils';
import { pessoasEndpoint } from '@/api/endpoints/pessoas';
import { basicosEndpoint } from '@/api/endpoints/basicos';
import { projetosEndpoint } from '@/api/endpoints/projetos';

export function usePessoasApi() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();

    const error = ref(null);

    const pessoaJuridica = ref({});
    const tiposPessoasJuridicas = ref([])
    const pessoaFisica = ref({});
    const coordenacoes = ref([]);
    const subcoordenacoes = ref([]);
    const listaPessoasJuridicas = ref([]);
    const listaPessoasJuridicasPaginado = ref([]);
    const listaPessoasFisicas = ref([]);
    const meusPagamentos = ref([]);
    const passagensEmitidas = ref([]);
    const relatoriosViagens = ref([]);
    const listaMotoristas = ref([]);
    const motorista = ref({});
    const totalMotoristas = ref(0);
    const totalPessoasFisicas = ref(0);
    const totalPessoasJuridicas = ref(0);
    const listaInstituicoesApoiadas = ref([]);
    const pessoaJuridicaEdicao = ref({});
    const projetosPorPessoaFisica = ref([]);

    const carregarPessoasJuridicasPaginado = async (page = 1, size = 20, filtro = '', estrangeiro = null) => {
        try {
            const params = new URLSearchParams({ page, size });
            if (filtro && filtro.trim() !== '') params.append('filtro', filtro.trim());
            if (estrangeiro !== null) params.append('estrangeiro', estrangeiro);
            const response = await getDados(`${pessoasEndpoint.pessoasJuridicas.listarPaginado}?${params}`);
            listaPessoasJuridicasPaginado.value = response.value.items;
            totalPessoasJuridicas.value = response.value.total;
        } catch (err) {
            handleApiError(err, {
                defaultMessage: 'Não foi possível carregar a lista de pessoas jurídicas',
            });
        }
    };

    const carregarPessoasFisicas = async (page = 1, size = 20, nome = '') => {
        try {
            const params = new URLSearchParams({ page, size });
            if (nome && nome.length > 1) params.append('search', nome);
            const response = await getDados(`${pessoasEndpoint.pessoasFisicas.listar}?${params}`);
            listaPessoasFisicas.value = response.value.items;
            totalPessoasFisicas.value = response.value.total;
        } catch (err) {
            handleApiError(err, {
                defaultMessage: 'Não foi possível carregar a lista de pessoas físicas',
            });
        }
    };

    const carregarMotoristas = async (page = 1, size = 20, nome = '') => {
        try {
            const params = new URLSearchParams({ page, size });
            if (nome.length > 3) params.append('nome', nome);
            const response = await getDados(`${pessoasEndpoint.carteiraMotorista.listar}?${params}`);
            listaMotoristas.value = response.value.items;
            totalMotoristas.value = response.value.total;
        } catch (err) {
            handleApiError(err, {
                defaultMessage: 'Não foi possível carregar os dados dos motoristas',
                unauthorizedMessage: 'Usuário não tem permissão para acessar a lista de motoristas',
            });
        }
    }

    const carregarMotorista = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.carteiraMotorista.obter(pessoaFisicaId));
            motorista.value = response.value;
        } catch (err) {
            handleApiError(err, {
                defaultMessage: 'Não foi possível carregar os dados do motorista',
            });
        }
    }

    const carregarMotoristasPorFiltro = async (query = '') => {
        try {
            const url = `${pessoasEndpoint.carteiraMotorista.listarPorFiltro}?nome=${query}`;
            const response = await getDados(url);
            listaMotoristas.value = response.value;
            return response.value;
        } catch (err) {
            error.value = handleApiError(err);
            return [];
        }
    }

    const carregarProjetosPorPessoaFisica = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasFisicas.listarProjetosPorPessoaFisica(pessoaFisicaId));
            projetosPorPessoaFisica.value = response.value;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarPessoaFisica = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasFisicas.obter(pessoaFisicaId));
            pessoaFisica.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const carregarPessoaJuridica = async (pessoaJuridicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasJuridicas.obter(pessoaJuridicaId));
            pessoaJuridica.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const carregarPessoaJuridicaPorCnpj = async (cnpj) => {
        try {
            const response = await getDados(`${pessoasEndpoint.pessoasJuridicas.obterPorCnpj}?cnpj=${cnpj}`);
            pessoaJuridica.value = response.value;
        } catch (err) {
            showLogConsole(err);
            throw err;
        }
    }

    const carregarPessoasJuridicas = async () => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasJuridicas.listar);
            listaPessoasJuridicas.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

        const carregarPessoasJuridicasEstrangeiras = async () => {
            try {
                const response = await getDados(`${pessoasEndpoint.pessoasJuridicas.listar}?estrangeiro=true`);
                listaPessoasJuridicas.value = response.value;
                return response.value;
            } catch (err) {
                error.value = handleApiError(err);
                return [];
            }
        }

    const carregarPessoasJuridicasPorTipo = async (tipoPessoaJuridicaIds) => {
        try {
            const response = await getDados(`${pessoasEndpoint.pessoasJuridicas.listar}?tipo_pessoa_juridica_id=${tipoPessoaJuridicaIds}`);
            listaPessoasJuridicas.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const buscarPessoasJuridicasPorFiltro = async (tipoPessoaJuridicaIds, query = '', estrangeiro = null) => {
        try {
            const params = new URLSearchParams();
            if (query) params.append('filtro', query);
            if (tipoPessoaJuridicaIds) params.append('tipo_pessoa_juridica_id', tipoPessoaJuridicaIds);
            if (estrangeiro !== null) params.append('estrangeiro', estrangeiro);
            const response = await getDados(`${pessoasEndpoint.pessoasJuridicas.listarPorFiltro}?${params}`);
            return response.value;
        } catch (err) {
            error.value = handleApiError(err);
            return [];
        }
    }

    const carregarTiposPessoasJuridicas = async () => {
        try {
            const response = await getDados(`${basicosEndpoint.tiposPessoasJuridicas.listar}`);
            tiposPessoasJuridicas.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const carregarCoordenacoes = async (pessoaFisicaId) => {
        try {
            const response = await getDados(`${pessoasEndpoint.pessoasFisicas.obterCoordenacoes(pessoaFisicaId)}`);
            coordenacoes.value = response.value.coordenacoes;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const carregarSubcoordenacoes = async (pessoaFisicaId) => {
        try {
            const response = await getDados(`${pessoasEndpoint.pessoasFisicas.obterSubcoordenacoes(pessoaFisicaId)}`);
            subcoordenacoes.value = response.value.coordenacoes;
        } catch (err) {
            error.value = handleApiError(err);
        }
    }

    const carregarMeusPagamentos = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasFisicas.obterMeusPagamentos(pessoaFisicaId));
            meusPagamentos.value = response.value;
        } catch (err) {
            handleApiError(err, { defaultMessage: 'Não foi possível carregar os pagamentos' });
        }
    };

    const carregarPassagensEmitidas = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasFisicas.obterPassagensEmitidas(pessoaFisicaId));
            passagensEmitidas.value = response.value;
        } catch (err) {
            handleApiError(err, { defaultMessage: 'Não foi possível carregar as passagens emitidas' });
        }
    };

    const carregarRelatoriosSolicitacoes = async (pessoaFisicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasFisicas.listarRelatoriosSolicitacoes(pessoaFisicaId));
            relatoriosViagens.value = response.value;
        } catch (err) {
            handleApiError(err, { defaultMessage: 'Não foi possível carregar os relatórios de solicitações' });
        }
    };

    const carregarInstituicoesApoiadas = async () => {
        try {
            const response = await getDados(projetosEndpoint.instituicaoApoiada.listar);
            listaInstituicoesApoiadas.value = response.value;
        } catch (err) {
            handleApiError(err, { defaultMessage: 'Não foi possível carregar as instituições apoiadas' });
        }
    }

    const carregarPessoaJuridicaParaEdicao = async (pessoaJuridicaId) => {
        try {
            const response = await getDados(pessoasEndpoint.pessoasJuridicas.obter(pessoaJuridicaId));
            pessoaJuridicaEdicao.value = response.value;
        } catch (err) {
            handleApiError(err, { defaultMessage: 'Não foi possível carregar os dados da pessoa jurídica' });
        }
    }

    const buscarPessoasFisicasPorFiltro = async (query = '') => {
        try {
            const url = `${pessoasEndpoint.pessoasFisicas.listarPorFiltro}?search=${query}`;
            const response = await getDados(url);
            listaPessoasFisicas.value = response.value;
            return response.value;
        } catch (err) {
            error.value = handleApiError(err);
            return [];
        }
    }

    return {
        pessoaFisica,
        pessoaJuridica,
        tiposPessoasJuridicas,
        coordenacoes,
        subcoordenacoes,
        listaPessoasJuridicas,
        listaPessoasJuridicasPaginado,
        listaPessoasFisicas,
        meusPagamentos,
        passagensEmitidas,
        relatoriosViagens,
        listaMotoristas,
        motorista,
        totalMotoristas,
        totalPessoasFisicas,
        totalPessoasJuridicas,
        listaInstituicoesApoiadas,
        pessoaJuridicaEdicao,
        projetosPorPessoaFisica,

        error,

        carregarPessoasJuridicasPaginado,
        carregarPessoasFisicas,
        carregarMotoristas,
        carregarMotorista,
        carregarPessoaFisica,
        carregarPessoaJuridica,
        carregarPessoaJuridicaPorCnpj,
        carregarPessoasJuridicas,
            carregarPessoasJuridicasEstrangeiras,
        carregarPessoasJuridicasPorTipo,
        carregarTiposPessoasJuridicas,
        carregarInstituicoesApoiadas,
        carregarPessoaJuridicaParaEdicao,
        carregarCoordenacoes,
        carregarSubcoordenacoes,
        buscarPessoasJuridicasPorFiltro,
        carregarMeusPagamentos,
        carregarPassagensEmitidas,
        carregarRelatoriosSolicitacoes,
        buscarPessoasFisicasPorFiltro,
        carregarMotoristasPorFiltro,
        carregarProjetosPorPessoaFisica,
    }
}
